import { Box, Flex, Text } from '@chakra-ui/layout';
import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/menu';
import {
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Tag,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import type { TypeSortInfo } from '@inovua/reactdatagrid-community/types';
import { IconArrowRotateLeftRight } from 'central-icons/IconArrowRotateLeftRight';
import { IconBookmark } from 'central-icons/IconBookmark';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import { IconFileDownload } from 'central-icons/IconFileDownload';
import { IconPageEdit } from 'central-icons/IconPageEdit';
import { IconStop } from 'central-icons/IconStop';
import { formatDistanceToNow, intlFormat } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isRoleRevelioAdmin, useGetLoggedInUser } from '@revelio/auth';
import {
  PagePaths,
  PageTitles,
  ReactDataGridLoading,
  WithTooltip,
} from '@revelio/core';
import { Button, FormField, Input } from '@revelio/ui';

import { showAlertDialog } from '../../shared/components/alert-dialog';
import '../../styles/table-custom.css';

import DashboardPage from '../DashboardPage';
import {
  cancelDataBuilderRun,
  fetchDataBuilderStatus,
} from './api/data-builder-api';
import type {
  DataBuilderStatus as DataBuilderApiStatus,
  DataBuilderPayload,
  DataBuilderStatusGetResponse,
} from './api/data-builder-api.types';
import { hydrateStoreFromPayload } from './api/hydrate-store-from-payload';
import { saveDataBuilderDataset } from './api/save-dataset-api';
import { type UserOption, UserSelector } from './components/user-selector';
import { DataBuilderStatusDetailsModal } from './DataBuilderStatusDetailsModal';
import { dataBuilderStore } from './data-builder.store';
import { DataBuilderPaths } from './steps/data-builder-steps';

export const defaultTagColors = { bgColor: 'gray.100', color: 'gray.600' };

const POLL_MS = 4 * 60 * 1000;

export const dataBuilderTagStatusColorMap: Partial<
  Record<DataBuilderApiStatus, { bgColor: string; color: string }>
> = {
  RUNNING: {
    bgColor: 'blue.100',
    color: 'blue.600',
  },
  FAILED: {
    bgColor: 'red.100',
    color: 'red.600',
  },
  FINISHED: {
    bgColor: 'green.100',
    color: 'green.600',
  },
  FINISHED_WITH_EMPTY: {
    bgColor: 'green.100',
    color: 'green.600',
  },
  CANCELLED: {
    bgColor: 'gray.100',
    color: 'gray.600',
  },
};

function isFinishedDataBuilderStatus(status: DataBuilderApiStatus): boolean {
  return status === 'FINISHED' || status === 'FINISHED_WITH_EMPTY';
}

function isFinishedWithEmptyStatus(status: DataBuilderApiStatus): boolean {
  return status === 'FINISHED_WITH_EMPTY';
}

export function getDataBuilderStatusLabel(
  status: DataBuilderApiStatus
): string {
  if (isFinishedWithEmptyStatus(status)) {
    return 'FINISHED *';
  }
  return status;
}

export function isDeliveryDownloadable(
  status: DataBuilderApiStatus,
  url: string | null
): boolean {
  if (!isFinishedDataBuilderStatus(status)) {
    return false;
  }
  if (url === null || url.trim().length === 0) {
    return false;
  }
  return true;
}

function hasDownloadDeliveryMethod(payload: DataBuilderPayload): boolean {
  return payload.deliverables.some(
    (deliverable) => deliverable.delivery.type === 'download'
  );
}

type SaveConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  defaultName: string;
  payload: DataBuilderPayload;
};

const SaveConfigModal = ({
  isOpen,
  onClose,
  defaultName,
  payload,
}: SaveConfigModalProps) => {
  const toast = useToast();
  const [configName, setConfigName] = useState(defaultName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfigName(defaultName);
    }
  }, [isOpen, defaultName]);

  const trimmedName = configName.trim();

  const handleSave = () => {
    if (trimmedName.length === 0) {
      return;
    }

    setSaving(true);
    void (async () => {
      try {
        await saveDataBuilderDataset({ name: trimmedName, payload });
        toast({
          title: 'Delivery config saved',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        onClose();
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Unknown error occurred';
        toast({
          title: 'Failed to save delivery config',
          description: message,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">Save Delivery Config</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormField
            label="Config name"
            errors={
              trimmedName.length === 0 ? ['A name is required'] : undefined
            }
          >
            <Input
              value={configName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfigName(e.target.value)
              }
              placeholder="Enter config name"
              data-testid="data_builder_v2_save_config_name_input"
              autoFocus
            />
          </FormField>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving || trimmedName.length === 0}
            data-testid="data_builder_v2_save_config_confirm"
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

type DataBuilderStatusRowMenuProps = {
  id: number;
  name: string;
  status: DataBuilderApiStatus;
  url: string | null;
  payload: DataBuilderPayload;
  onCanceled: () => void;
};

const DataBuilderStatusRowMenu = ({
  id,
  name,
  status,
  url,
  payload,
  onCanceled,
}: DataBuilderStatusRowMenuProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const saveModal = useDisclosure();
  const canDownload = isDeliveryDownloadable(status, url);
  const canCancel = status === 'RUNNING';
  const downloadUnavailableLabel = (() => {
    if (!hasDownloadDeliveryMethod(payload)) {
      return 'Download is only available for deliveries using Download method';
    }
    if (!isFinishedDataBuilderStatus(status)) {
      return 'Download is only available when delivery is successful';
    }
    if (isDeliveryDownloadable(status, url)) {
      return '';
    }
    return 'Download is unavailable for this delivery';
  })();

  const handleDownload = () => {
    if (url !== null && url.trim().length > 0) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDuplicateEdit = () => {
    const hydratedState = hydrateStoreFromPayload(payload);
    dataBuilderStore.getState().reset();
    dataBuilderStore.setState({ ...hydratedState, loadedSavedDatasetId: null });
    navigate(`/${PagePaths.DATA_BUILDER}/${DataBuilderPaths.DATASET}`);
  };

  const handleCancel = () => {
    showAlertDialog({
      alertType: 'confirm',
      isOpen: true,
      headerText: 'Cancel this run?',
      bodyContent: (
        <span>
          This will stop the run immediately. Any partial results will be
          discarded and the delivery will not complete.
        </span>
      ),
      footerAcceptIsDestructive: true,
      footerAcceptText: 'Yes, Cancel Run',
      onAccept: async () => {
        try {
          await cancelDataBuilderRun({ id });
          toast({
            title: 'Run canceled',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
          onCanceled();
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : 'Unknown error occurred';
          toast({
            title: 'Failed to cancel run',
            description: message,
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
      },
    });
  };

  return (
    <>
      <Menu placement="auto-end">
        <MenuButton
          as={IconButton}
          aria-label="Row actions"
          icon={<Icon as={IconDotGrid1x3Vertical} />}
          size="xs"
          colorScheme="gray"
          data-testid="data_builder_v2_status_row_menu_button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            e.stopPropagation()
          }
        />
        <Portal>
          <MenuList
            zIndex={101}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
            }}
          >
            <MenuItem
              icon={<Icon as={IconBookmark} boxSize={4} />}
              data-testid="data_builder_v2_status_save_config"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                saveModal.onOpen();
              }}
            >
              Save Delivery Config
            </MenuItem>
            <MenuItem
              icon={<Icon as={IconPageEdit} boxSize={4} />}
              data-testid="data_builder_v2_status_duplicate_edit"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleDuplicateEdit();
              }}
            >
              Duplicate & Edit
            </MenuItem>
            {canCancel && (
              <MenuItem
                icon={<Icon as={IconStop} boxSize={4} />}
                isDisabled={!canCancel}
                data-testid="data_builder_v2_status_cancel_run"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                Cancel Run
              </MenuItem>
            )}
            <Tooltip
              label={downloadUnavailableLabel}
              isDisabled={canDownload}
              hasArrow
              placement="left"
            >
              <MenuItem
                icon={<Icon as={IconFileDownload} boxSize={4} />}
                isDisabled={!canDownload}
                data-testid="data_builder_v2_status_download"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleDownload();
                }}
              >
                Download
              </MenuItem>
            </Tooltip>
          </MenuList>
        </Portal>
      </Menu>
      <SaveConfigModal
        isOpen={saveModal.isOpen}
        onClose={saveModal.onClose}
        defaultName={name}
        payload={payload}
      />
    </>
  );
};

type StatusGridRow = {
  id: number;
  name: string;
  client: string;
  created_at: string;
  updated_at: string;
  status: DataBuilderApiStatus;
  url: string | null;
  payload: DataBuilderPayload;
};

export const DataBuilderStatus = () => {
  const { loggedInUser } = useGetLoggedInUser();
  const showUserSelector = isRoleRevelioAdmin(loggedInUser?.role);

  const [selectedUserOption, setSelectedUserOption] = useState<UserOption>();
  const [rows, setRows] = useState<DataBuilderStatusGetResponse>([]);
  const [fetching, setFetching] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(() => new Date());
  const [timeDistance, setTimeDistance] = useState('');
  const [selectedRow, setSelectedRow] = useState<StatusGridRow | null>(null);

  /** Default the picker to the logged-in user (display only — no user_id is
   * sent unless an admin actively picks someone else). */
  useEffect(() => {
    if (loggedInUser?.id) {
      const id = loggedInUser.id;
      setSelectedUserOption((prev) => {
        if (prev) return prev;
        return {
          value: id,
          label: loggedInUser.name || '',
          group: loggedInUser.client_name || '',
          secondaryLabel: loggedInUser.email || '',
        };
      });
    }
  }, [loggedInUser]);

  const userIdParam =
    selectedUserOption && selectedUserOption.value !== loggedInUser?.id
      ? selectedUserOption.value
      : undefined;

  const loadRows = async (overrideUserId?: string) => {
    setFetching(true);
    try {
      const next = await fetchDataBuilderStatus(overrideUserId);
      setRows(next);
      setLastUpdate(new Date());
    } catch (error) {
      console.error(error);
    } finally {
      setFetching(false);
      setHasFetchedOnce(true);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: poll uses initial loadRows; setState is stable
  useEffect(() => {
    void loadRows(userIdParam);
    const poll = window.setInterval(() => void loadRows(userIdParam), POLL_MS);
    return () => window.clearInterval(poll);
  }, [userIdParam]);

  useEffect(() => {
    setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));
    const id = window.setInterval(() => {
      setTimeDistance(formatDistanceToNow(lastUpdate, { addSuffix: true }));
    }, 60_000);
    return () => window.clearInterval(id);
  }, [lastUpdate]);

  const dataSource: StatusGridRow[] = rows.map((row) => ({
    id: row.id,
    name: row.name,
    client: row.client,
    created_at: row.date_created,
    updated_at: row.date_updated,
    status: row.status,
    url: row.url ?? null,
    payload: row.payload as DataBuilderPayload,
  }));

  const defaultSortInfo: TypeSortInfo = { name: 'created_at', dir: -1 };

  const columns = [
    {
      name: 'id',
      header: 'ID',
      minWidth: 30,
      maxWidth: 80,
      defaultFlex: 0.5,
      render: ({ value }: { value: number }) => (
        <div data-testid={`${value}_data_builder_v2_status_row`}>{value}</div>
      ),
    },
    {
      name: 'name',
      header: 'Name',
      minWidth: 30,
      defaultFlex: 1,
    },
    {
      name: 'client',
      header: 'Client',
      minWidth: 30,
      defaultFlex: 0.7,
    },
    {
      name: 'created_at',
      header: 'Creation',
      minWidth: 30,
      defaultFlex: 1.1,
      render: ({ value }: { value: string }) => (
        <div>
          {intlFormat(new Date(value), {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneName: 'short',
          })}
        </div>
      ),
    },
    {
      name: 'updated_at',
      header: 'Updated',
      minWidth: 30,
      defaultFlex: 0.5,
      render: ({ value }: { value: string }) => {
        const date = new Date(value);
        const fullDateTime = intlFormat(date, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneName: 'short',
        });
        return (
          <Tooltip label={fullDateTime} hasArrow placement="top">
            <div>{formatDistanceToNow(date, { addSuffix: true })}</div>
          </Tooltip>
        );
      },
    },
    {
      name: 'status',
      minWidth: 30,
      defaultFlex: 0.7,
      header: () => (
        <div>
          <span>Status</span>
          <IconButton
            aria-label="Refresh status"
            icon={<IconArrowRotateLeftRight size={14} />}
            colorScheme="gray"
            variant="outline"
            size="xs"
            ml={2}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              void loadRows(userIdParam);
            }}
          />
        </div>
      ),
      render: ({
        value,
        data,
      }: {
        value: DataBuilderApiStatus;
        data: StatusGridRow;
      }) => {
        const palette = dataBuilderTagStatusColorMap[value] ?? defaultTagColors;
        return (
          <Flex justifyContent="space-between" alignItems="center" gap={2}>
            <WithTooltip
              showCondition={isFinishedWithEmptyStatus(value)}
              label="Finished but some results have no data. This is typically caused by adding filters that are too specific, or by choosing very small companies that have no public profiles, job postings, or sentiment reviews."
              placement="bottom"
            >
              <Tag
                bg={palette.bgColor}
                color={palette.color}
                borderRadius="4px"
                fontWeight="semibold"
                minWidth="94px"
                textAlign="center"
                py={1}
                justifyContent="center"
              >
                {getDataBuilderStatusLabel(value)}
              </Tag>
            </WithTooltip>
            <DataBuilderStatusRowMenu
              id={data.id}
              name={data.name}
              status={value}
              url={data.url}
              payload={data.payload}
              onCanceled={() => loadRows(userIdParam)}
            />
          </Flex>
        );
      },
    },
  ];

  return (
    <DashboardPage
      title={[PageTitles.DELIVERABLES, PageTitles.DELIVERABLES_STATUS]}
      loading={!hasFetchedOnce && fetching}
    >
      {showUserSelector && (
        <Box display="flex" justifyContent="space-between" alignItems="end">
          <UserSelector
            selectedUserOption={selectedUserOption}
            setSelectedUserOption={setSelectedUserOption}
            resetLabel="Reset to my runs"
            testId="data-builder-status-user-selector"
            selectableClientGroups={false}
          />
        </Box>
      )}
      <Box
        bg="white"
        rounded="lg"
        h={`calc(100vh - ${showUserSelector ? '169px' : '100px'})`}
        p={4}
        data-testid="data-builder-status-grid"
      >
        <ReactDataGrid
          columns={columns}
          dataSource={dataSource}
          style={{ minHeight: '100%' }}
          className="InovuaReactDataGrid--theme-default-light"
          rowHeight={50}
          emptyText="No deliveries yet"
          theme="rl-light"
          defaultSortInfo={defaultSortInfo}
          showCellBorders={false}
          showZebraRows={false}
          showColumnMenuTool={false}
          loading={fetching}
          renderLoadMask={ReactDataGridLoading}
          onRowClick={({ data }: { data: StatusGridRow }) =>
            setSelectedRow(data)
          }
        />
      </Box>
      <Flex alignItems="center" justifyContent="flex-end">
        <Text fontSize="xs" pt={2} opacity={0.5}>
          <Icon as={IconArrowRotateLeftRight} mr={1} top="1px" pos="relative" />{' '}
          <span>Last updated: {timeDistance}</span>
        </Text>
      </Flex>
      {selectedRow !== null && (
        <DataBuilderStatusDetailsModal
          isOpen
          onClose={() => setSelectedRow(null)}
          name={selectedRow.name}
          client={selectedRow.client}
          status={selectedRow.status}
          url={selectedRow.url}
          payload={selectedRow.payload}
        />
      )}
    </DashboardPage>
  );
};
